import { useState, useEffect } from 'react';

export const T = {
  bg: 'var(--bg)',
  sidebar: 'var(--sidebar)',
  card: 'var(--card)',
  cardBorder: 'var(--card-border)',
  cardInner: 'var(--card-inner)',
  primary: 'var(--primary)',
  primaryDim: 'var(--primary-dim)',
  primaryBorder: 'var(--primary-border)',
  owner: 'var(--owner)',
  ownerDim: 'var(--owner-dim)',
  ownerBorder: 'var(--owner-border)',
  warning: 'var(--warning)',
  warningDim: 'var(--warning-dim)',
  warningBorder: 'var(--warning-border)',
  success: 'var(--success)',
  successDim: 'var(--success-dim)',
  successBorder: 'var(--success-border)',
  danger: 'var(--danger)',
  dangerDim: 'var(--danger-dim)',
  dangerBorder: 'var(--danger-border)',
  textPrimary: 'var(--text-primary)',
  textSecondary: 'var(--text-secondary)',
  textMuted: 'var(--text-muted)',
  divider: 'var(--divider)',
  shadow: 'var(--shadow)',
  shadowLg: 'var(--shadow-lg)',
};

// Named tones replace the old `color + '15'` alpha-suffix trick, which can't
// work now that colors are var() references rather than hex literals.

export const TONES = {
  primary: { color: T.primary, bg: T.primaryDim, border: T.primaryBorder },
  owner: { color: T.owner, bg: T.ownerDim, border: T.ownerBorder },
  success: { color: T.success, bg: T.successDim, border: T.successBorder },
  warning: { color: T.warning, bg: T.warningDim, border: T.warningBorder },
  danger: { color: T.danger, bg: T.dangerDim, border: T.dangerBorder },
  muted: { color: T.textMuted, bg: T.cardInner, border: T.cardBorder },
};

export const tone = (name) => TONES[name] || TONES.muted;

// ── Scale ─────────────────────────────────────────────
// Sizes were chosen per call site for a long time: the panel used eleven
// different font sizes between 10.5 and 15px, which is most of why a page of
// cards read as noise rather than as a hierarchy. New and edited work uses
// these and nothing else.
//
// Deliberately few. Every size here has one job, and if a new piece of text
// does not fit one of them it is probably the same thing as something that
// already exists.

export const F = {
  micro: 11,      // counts, timestamps — the smallest line worth reading
  caption: 12,    // field labels, helper text under an input
  small: 12.5,    // secondary body: the second line of a list row
  body: 13.5,     // default
  lead: 15,       // card titles
  title: 18,      // page title on a phone
  display: 22,    // page title
  figure: 26,     // the one number a card exists to show
};

export const S = { xs: 6, sm: 10, md: 14, lg: 18, xl: 24 };

export const R = { sm: 8, md: 10, lg: 14, pill: 999 };

/**
 * How tall the mobile top bar is.
 *
 * Needed as a token rather than a magic number because the filter row sticks
 * to the bottom of it, and a guess that is 2px out either overlaps the bar or
 * leaves a gap that list rows slide through.
 */
export const TOPBAR_H = 'var(--topbar-h)';

// ── Owner response stats ─────────────────────────────────
// `responseStats` is denormalised onto the user document by
// onBookingStatusChange in functions/index.js, because renters cannot query
// another user's bookings and the figure has to be readable where it is shown.
//
// A request counts as answered if the owner approved or declined it. Letting it
// expire is the only outcome that counts against them — declining is a
// legitimate answer, and scoring it as a failure would push owners into
// accepting bookings they cannot honour.
//
// Requests the renter withdrew before the owner replied are never counted at
// all; the trigger excludes them from `received`.

export const THEME_CSS = `
  :root {
    /* Deepened from the app's #00BFA5 brand teal so white text on a primary
       fill (buttons, chat bubbles) clears WCAG AA on a light background. */
    --primary: #00806D;
    --primary-dim: #00806D14;
    --primary-border: #00806D33;
    --owner: #E8590C;
    --owner-dim: #E8590C14;
    --owner-border: #E8590C33;
    --warning: #C77700;
    --warning-dim: #C7770014;
    --warning-border: #C7770033;
    --success: #0E9F6E;
    --success-dim: #0E9F6E14;
    --success-border: #0E9F6E33;
    --danger: #DC2626;
    --danger-dim: #DC262614;
    --danger-border: #DC262633;

    --bg: #F6F7F9;
    --sidebar: #FFFFFF;
    --card: #FFFFFF;
    --card-border: #E5E8EC;
    --card-inner: #F6F7F9;
    --text-primary: #0F1729;
    --text-secondary: #55617A;
    --text-muted: #8892A6;
    --divider: #ECEEF2;
    --shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    --shadow-lg: 0 12px 32px rgba(16, 24, 40, 0.12);
    --backdrop: rgba(16, 24, 40, 0.45);
    /* 30px of content + 11px padding top and bottom + the 1px border. */
    --topbar-h: calc(53px + env(safe-area-inset-top));
    /* 20px icon + 3px gap + a 12px label line + 17px padding + the border. */
    --bottomnav-h: calc(53px + env(safe-area-inset-bottom));
    color-scheme: light;
  }

  :root[data-theme="dark"] {
    --primary: #00BFA5;
    --primary-dim: #00BFA51F;
    --primary-border: #00BFA540;
    --owner: #FB923C;
    --owner-dim: #FB923C1F;
    --owner-border: #FB923C40;
    --warning: #FBBF24;
    --warning-dim: #FBBF241F;
    --warning-border: #FBBF2440;
    --success: #34D399;
    --success-dim: #34D3991F;
    --success-border: #34D39940;
    --danger: #F87171;
    --danger-dim: #F871711F;
    --danger-border: #F8717140;

    --bg: #0B0C0E;
    --sidebar: #131519;
    --card: #131519;
    --card-border: #24272E;
    --card-inner: #1A1D22;
    --text-primary: #F2F4F7;
    --text-secondary: #A0A8B8;
    --text-muted: #6C7488;
    --divider: #24272E;
    --shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
    --shadow-lg: 0 12px 32px rgba(0, 0, 0, 0.55);
    --backdrop: rgba(0, 0, 0, 0.6);
    color-scheme: dark;
  }
`;

export function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return window.localStorage.getItem('saikapp-admin-theme') || 'light';
  });
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem('saikapp-admin-theme', theme);
  }, [theme]);
  return [theme, () => setTheme(t => (t === 'dark' ? 'light' : 'dark'))];
}

// ── Brand mark ───────────────────────────────────────────
// The app's own scooter icon, so the panel reads as the same product.
